import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import HomeChat from '../../components/HomeChat'

interface CommunityUser {
  id: string; name: string; email?: string; role?: string; community_id?: string;
}

interface Community {
  id: string; name: string; slug: string; status: string;
}

// Generate a consistent color from a name
function nameColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 45%, 55%)`
}

export default function UserPublicPage({ overrideCommunitySlug, overrideUserId }: { overrideCommunitySlug?: string; overrideUserId?: string } = {}) {
  const params = useParams<{ communitySlug: string; userId: string }>()
  const communitySlug = overrideCommunitySlug || params.communitySlug
  const userId = overrideUserId || params.userId

  // Fetch community info
  const { data: communities } = useQuery({
    queryKey: ['communities'],
    queryFn: async () => {
      const res = await fetch('/api/communities')
      if (!res.ok) return []
      const data = await res.json()
      return (data.communities || data) as Community[]
    },
  })

  const community = communities?.find(p => p.slug === communitySlug)

  // Fetch users for this community
  const { data: users } = useQuery({
    queryKey: ['communityUsers', community?.id],
    queryFn: async () => {
      if (!community) return []
      const res = await fetch(`/api/communities/${community.id}/users`)
      if (!res.ok) return []
      const data = await res.json()
      return (data.users || data) as CommunityUser[]
    },
    enabled: !!community,
  })

  const user = users?.find(u => u.id === userId || u.name.toLowerCase() === userId?.toLowerCase())
  const displayName = user ? user.name[0].toUpperCase() + user.name.slice(1) : userId || ''
  const color = nameColor(displayName)
  const initial = displayName[0] || '?'

  if (!communities) {
    return <div className="flex items-center justify-center h-64 text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  }

  if (!community) {
    return <div className="flex items-center justify-center h-64 text-sm" style={{ color: 'var(--text-muted)' }}>Community not found</div>
  }

  return (
    <div className="overflow-y-auto h-full" style={{ background: 'var(--bg-body)' }}>
      {/* Hero */}
      <div
        className="relative px-8 py-12"
        style={{
          background: `linear-gradient(135deg, ${color}22 0%, var(--bg-body) 60%)`,
          borderBottom: `2px solid ${color}44`,
        }}
      >
        <Link to="/neighborhood" className="text-xs no-underline mb-4 inline-block" style={{ color: 'var(--text-muted)' }}>
          ← The Neighborhood
        </Link>

        <div className="flex items-center gap-6">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold shrink-0"
            style={{ background: `${color}33`, color, border: `2px solid ${color}66` }}
          >
            {initial}
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color }}>
              {displayName}
            </h1>
            <div className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {community.name}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left: Wall (empty template for now) + Chat */}
          <div className="md:col-span-2 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              The Wall
            </h2>
            <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 24, opacity: 0.3, marginBottom: 8 }}>✦</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                This space is waiting to be filled.
              </div>
            </div>

            {/* Home Chat */}
            <div className="mt-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                Chat
              </h2>
              <HomeChat
                personaName={user?.name || userId || ''}
                displayName={displayName}
                theme={{
                  accentColor: color,
                  bgGradient: `linear-gradient(135deg, #0d1117 0%, ${color}11 50%, #0d1117 100%)`,
                  myBubbleBg: `${color}15`,
                  myBubbleBorder: `${color}66`,
                  visitorBubbleBg: '#161b22',
                  inputBg: '#0d1117',
                  inputFocusGlow: `${color}44`,
                }}
              />
            </div>
          </div>

          {/* Right: Info */}
          <div className="space-y-6">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                About
              </h2>
              <div className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Member of {community.name}
              </div>
            </div>

            {user?.email && (
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {user.email}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
